import appConfig from 'app.config'
import { CookieType } from 'constants/enums/cookieTypes'
import { DocumentTypes } from 'constants/enums/documentTypes'
import { LifeCycleStatus } from 'constants/enums/lifeCycleStatus'
import { ProductType } from 'constants/enums/productTypes'
import { UrlStatus } from 'constants/enums/signingUrlTypes'
import { TFunction } from 'i18next'
import { IApplicationInformation } from 'interfaces/i-application-information'
import { IDocuments } from 'interfaces/i-documents'
import { IApplicationUrlsDto } from 'interfaces/i-signing-urls'
import queryString from 'query-string'
import authenticationService from 'services/authServices'

/**
 * Checks if application is valid on LifeCycleStatus
 * @param status LifeCycleStatus from application
 * @returns boolean
 */
export const CheckIsValidApplication = (
	status: LifeCycleStatus | undefined,
	product: ProductType | undefined,
) => {
	switch (status) {
		case LifeCycleStatus.Canceled:
		case LifeCycleStatus.TimedOut:
		case LifeCycleStatus.Duplicated:
		case LifeCycleStatus.Invalid:
			return false
		case LifeCycleStatus.Disbursed:
			return appConfig.country === 'de'
		case LifeCycleStatus.Denied:
			return product === ProductType.EspCreditCard
		default:
			return true
	}
}

/**
 * Function to check if application lifecycle status matches any of the types in list
 * @param showOnStatus List of Lifecyclestatuses
 * @param applicationStatus LifecycleStatus which application is currently in
 * @returns true if application LifecycleStatus matches any of the listed showOnStatuses
 */
export const CheckIsLifeCycleStatus = (
	showOnStatus: LifeCycleStatus[],
	applicationStatus: LifeCycleStatus,
) => {
	return showOnStatus.includes(applicationStatus)
}

/**
 * Function to check if document collection contains a set of specific document types
 * @param documents document collection in application
 * @param documentTypes list of document types to check for
 * @returns true if ALL documentType exists in document collection
 */
export const CheckHasApplicationDocuments = (
	documents: IDocuments[],
	documentTypes: DocumentTypes[],
) => {
	let documentArr: DocumentTypes[] = []
	documents.forEach((doc) => {
		documentArr.push(doc.type)
	})
	return documentTypes.every((element) => documentArr.includes(element))
}

/**
 * Function that returns application query from sessionstorage
 * If application query is null return quesry value from url
 * @returns query string
 */
export const getQueryValues = () => {
	return queryString.parse(
		sessionStorage.getItem(CookieType.ApplicationQuery) ??
			window.location.search.toLowerCase(),
	)
}

/**
 * Checks if promissory note status is signed in application
 * OR query value in url has value 'success'
 * @param applicationData
 * @returns boolean
 */
export const isPnStatusSigned = (applicationData: IApplicationInformation) =>
	applicationData?.signingUrls?.pnSigning?.status! === UrlStatus.Signed ||
	getQueryValues().status === 'success'

/**
 * Checks if applicant consent status is signed in application
 * OR query value in url has value 'success'
 * @param applicationData
 * @returns boolean
 */
export const isApplicantConsentStatusSigned = (
	applicationData: IApplicationInformation,
) =>
	applicationData?.signingUrls?.applicantConsent?.status! ===
		UrlStatus.Signed || getQueryValues().status === 'success'

/**
 * Checks if spouse consent status is signed in application
 * OR query value in url has value 'success'
 * @param applicationData
 * @returns boolean
 */
export const isSpouseConsentStatusSigned = (
	applicationData: IApplicationInformation,
) =>
	applicationData?.signingUrls?.spouseConsent?.status! === UrlStatus.Signed ||
	getQueryValues().status === 'success'

/**
 * Checks if psd2 status is signed in application
 * OR query value in url has value 'success'
 * @param applicationData
 * @returns boolean
 */
export const isPsd2StatusSigned = (applicationData: IApplicationInformation) =>
	applicationData?.signingUrls?.psD2?.status! === UrlStatus.Signed ||
	getQueryValues().psd2status === 'success' ||
	getQueryValues().psd2 === 'ok'

/**
 * Checks if psd2 signing is mandatory in application
 * @param applicationData
 * @returns boolean
 */
export const isPsd2Mandatory = (applicationData: IApplicationInformation) =>
	applicationData?.signingUrls?.psD2?.isMandatory!

/**
 * Checks if ppi status is signed in application
 * OR query value in url has value 'success'
 * Used in Finnish application
 * @param applicationData
 * @returns boolean
 */
export const isPpiStatusSigned = (applicationData: IApplicationInformation) =>
	applicationData?.signingUrls?.ppiSigning?.status! === UrlStatus.Signed ||
	getQueryValues().ppistatus === 'success'

/**
 * Returns true if product type is one of the new Finish ones supporting base and margin rates.
 * Only used for Finland (as of end of october 2023)
 * @param applicationData
 * @returns
 */
export function showBaseAndMarginRatesFinland(
	applicationData: IApplicationInformation | undefined,
) {
	let isNewProduct = false
	const baseAndMarginProducts = [
		ProductType.FinCashLoan,
		ProductType.FinHalensLoan,
		ProductType.FinRefinLoan,
		ProductType.FinHalensLoanPpi,
		ProductType.FinCashLoanPpi,
		ProductType.FinRefinLoanPpi,
	]
	if (applicationData)
		isNewProduct = baseAndMarginProducts.includes(applicationData.product)

	return isNewProduct
}

/**
 * Updates signing urls for promissory notes with artifact.
 * @param oldUrl
 * @param applicationData
 * @param t
 * @returns url with artifact attached
 */
export const updateSigningUrlsWithArtifact = async (
	oldUrl: string,
	applicationData: IApplicationInformation,
	t: TFunction,
) => {
	try {
		let queryValues = queryString.parse(queryString.extract(oldUrl))
		let newArtifact: string = await authenticationService.getArtifact(
			queryValues.request_id as string,
			queryValues.task_id as string,
			[ProductType.NorCreditCard, ProductType.NorCreditCardGold].includes(
				applicationData.product,
			), //it is used only for Norway thus it is enough to check only Norwegian credit card products
			t,
		)
		if (queryValues.artifact) {
			let url = oldUrl.replace(
				queryValues.artifact as string,
				newArtifact,
			)
			return url
		}
		return `${oldUrl}&artifact=${newArtifact}`
	} catch (error) {
		//setError(getErrorWithFullMessage(error))
		//redirect to oldUrl for signing in if no artifact
		return oldUrl
	}
}

/**
 * Gets url for signing promissory notes with attached artifact.
 * Artifact enables signing directly and not needing to sign in with Signicat again.
 * @param applicationData
 * @param signingUrls
 * @param t
 */
export const onPnSignClick = (
	applicationData: IApplicationInformation,
	signingUrls: IApplicationUrlsDto,
	t: TFunction,
) => {
	const isGermanProduct = applicationData.product === ProductType.DeuCashLoan

	const url = isGermanProduct
		? applicationData.signingUrls.onlineIdentification.url
		: applicationData.isCoApplicant
			? signingUrls.coapplicantSigning!
			: signingUrls.applicantSigning!

	const isNorwegianProduct = [
		ProductType.NorRefin,
		ProductType.NorCreditCard,
		ProductType.NorCreditCardGold,
	].includes(applicationData.product)

	// Germany should NOT use artifact
	if (isGermanProduct) {
		window.location.href = url
		return
	}

	if (isNorwegianProduct && !isOldNorwegianUrl(url)) {
		window.location.href = url
	} else {
		updateSigningUrlsWithArtifact(url, applicationData, t)
			.then((data) => {
				if (data) {
					window.location.href = data
				}
			})
			// don´t show button
			.catch()
	}
}

// this logic should be removed after april 2026. then only "new" urls will be used and we should never add artifacts for norway
function isOldNorwegianUrl(url: string): boolean {
	return url.includes('/docaction/')
}

/**
 * Gets url for signing ppi with attached artifact.
 * Artifact enables signing directly and not needing to sign in with Signicat again.
 * @param signingUrls
 * @param applicationData
 * @param t
 */
export const onPpiSignClick = (
	signingUrls: IApplicationUrlsDto,
	applicationData: IApplicationInformation,
	t: TFunction,
) => {
	updateSigningUrlsWithArtifact(signingUrls?.ppiContract!, applicationData, t)
		.then((data) => {
			if (data) {
				window.location.href = data
			}
		})
		//don´t show button
		.catch()
}

/**
 * Redirect function where user is redirected to url.
 * @param url
 */
export const onClickRedirectToUrl = (url: string) => {
	window.location.href = url
}
